/* Phase 9 -- Public invite acceptance page.
 * Route: /invite/:token  (no auth required)
 * Flow: verify token -> show pre-filled card -> employee sets password ->
 * accept -> auto-login (session cookie + localStorage token) -> redirect
 * to /me/onboarding if instance exists, else /me.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatIST } from "@/lib/time";
import errorToast from "@/lib/errorToast";

function ErrorState({ title, body, cta = null }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto" data-testid="invite-error">
      <div className="glass-elevated p-8 text-center space-y-3">
        <div className="mx-auto h-12 w-12 rounded-full bg-rose-500/20 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-rose-300" />
        </div>
        <div className="font-heading text-lg uppercase tracking-[0.08em]">{title}</div>
        <p className="text-sm text-foreground/60">{body}</p>
        {cta}
      </div>
    </motion.div>
  );
}

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const hydrate = useAuth((s) => s.hydrate);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emp, setEmp] = useState(null);
  const [expires, setExpires] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [consent, setConsent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/hr/invite/verify/${token}`);
        if (alive) {
          setEmp(data.employee);
          setExpires(data.expires_at);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e?.message || "Invalid link");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const canSubmit = password.length >= 8 && password === confirm && consent && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/hr/invite/accept/${token}`, {
        password, confirm_password: confirm,
        phone: phone || undefined, dob: dob || undefined,
        consent: true,
      });
      if (data.token) localStorage.setItem("sharda_token", data.token);
      await hydrate();
      toast.success(t("invite.welcomeToast", { name: data.user?.name || "" }));
      // Look for an active onboarding instance -> land on wizard, else /me.
      try {
        await api.get("/hr/onboarding/instances/mine");
        nav("/me/onboarding", { replace: true });
      } catch {
        nav("/me", { replace: true });
      }
    } catch (e) {
      errorToast(e);
      setSubmitting(false);
    }
  };

  const switchLang = (l) => i18n.changeLanguage(l);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#040D1C] via-[#031028] to-[#003771] py-10 px-4">
      {/* Top strip: logo + language */}
      <div className="max-w-2xl mx-auto flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Sharda Power" className="h-9 w-9 rounded-lg" />
          <div>
            <div className="font-heading text-lg uppercase tracking-[0.14em] text-white">Sharda Energy OS</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Unified Business OS</div>
          </div>
        </div>
        <div className="flex gap-1 text-xs">
          <button onClick={() => switchLang("en")} className={`px-2 py-1 rounded ${i18n.language === "en" ? "bg-white/10 text-white" : "text-white/60"}`} data-testid="invite-lang-en">EN</button>
          <button onClick={() => switchLang("hi")} className={`px-2 py-1 rounded ${i18n.language === "hi" ? "bg-white/10 text-white" : "text-white/60"}`} data-testid="invite-lang-hi">HI</button>
        </div>
      </div>

      {loading ? (
        <div className="max-w-md mx-auto glass-elevated p-10 text-center">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-sky-300" />
          <div className="text-xs text-foreground/60 mt-3">{t("invite.checking")}</div>
        </div>
      ) : error ? (
        <ErrorState
          title={t("invite.errorTitle")}
          body={error}
          cta={<Button variant="outline" onClick={() => nav("/login")} data-testid="invite-goto-login">{t("invite.goToLogin")}</Button>}
        />
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto" data-testid="invite-accept-form">
          <div className="glass-elevated p-6 md:p-8 space-y-6">
            <div className="text-center space-y-1">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-primary flex items-center justify-center">
                <ShieldCheck className="h-7 w-7 text-white" />
              </div>
              <div className="font-heading text-2xl md:text-3xl uppercase tracking-[0.08em]">{t("invite.welcomeTitle", { name: emp?.name || "" })}</div>
              <p className="text-sm text-foreground/60">{t("invite.welcomeSubtitle")}</p>
              {expires && <p className="text-[11px] text-amber-300/80 mt-1">{t("invite.expires")} {formatIST(expires)}</p>}
            </div>

            {/* Locked identity card */}
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2" data-testid="invite-emp-card">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fName")}</div>
                  <div>{emp?.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fEmail")}</div>
                  <div className="truncate">{emp?.email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fRole")}</div>
                  <div><Badge variant="outline" className="text-[10px]">{emp?.role}</Badge></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fEmpCode")}</div>
                  <div className="font-mono">{emp?.employee_code || "--"}</div>
                </div>
                {emp?.designation && (<div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fDesignation")}</div>
                  <div>{emp.designation}</div>
                </div>)}
                {emp?.department && (<div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fDepartment")}</div>
                  <div className="capitalize">{emp.department}</div>
                </div>)}
                {emp?.joining_date && (<div>
                  <div className="text-[10px] uppercase tracking-wider text-foreground/50">{t("invite.fJoinDate")}</div>
                  <div>{emp.joining_date}</div>
                </div>)}
              </div>
            </div>

            {/* Editable */}
            <div className="space-y-3">
              <div>
                <Label>{t("invite.setPassword")}</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("invite.pwPlaceholder")} data-testid="invite-pw" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/50" data-testid="invite-pw-toggle">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-[10px] text-foreground/50 mt-1">{t("invite.pwHint")}</div>
              </div>
              <div>
                <Label>{t("invite.confirmPassword")}</Label>
                <div className="relative">
                  <Input type={showConfirm ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-pw-confirm" />
                  <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/50">
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirm && confirm !== password && (
                  <div className="text-[11px] text-rose-300 mt-1">{t("invite.pwMismatch")}</div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("invite.phone")}</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91-98xxxxxxxx" data-testid="invite-phone" />
                </div>
                <div>
                  <Label>{t("invite.dob")}</Label>
                  <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="invite-dob" />
                </div>
              </div>
            </div>

            {/* Consent */}
            <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="invite-consent-row">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="invite-consent" />
              <span className="text-foreground/70">
                {t("invite.consentPrefix")}{" "}
                <button type="button" onClick={() => setPolicyOpen(true)} className="text-sky-300 underline" data-testid="invite-open-policy">{t("invite.companyPolicies")}</button>
              </span>
            </label>

            <Button onClick={submit} disabled={!canSubmit} className="w-full bg-gradient-primary text-white" data-testid="invite-submit">
              {submitting ? (<><Loader2 className="h-3 w-3 animate-spin mr-2" /> {t("invite.settingUp")}</>) : (<><CheckCircle2 className="h-3 w-3 mr-2" /> {t("invite.completeSetup")}</>)}
            </Button>
            <p className="text-[11px] text-foreground/50 text-center mt-1" data-testid="invite-confirmation-note">
              {t("invite.confirmationEmailNote", { email: emp?.email || "" })}
            </p>
          </div>
        </motion.div>
      )}

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent data-testid="invite-policy-dialog">
          <DialogHeader><DialogTitle>{t("invite.companyPolicies")}</DialogTitle></DialogHeader>
          <div className="text-sm text-foreground/70 space-y-2 max-h-[60vh] overflow-y-auto">
            <p>{t("invite.policyIntro")}</p>
            <p className="text-foreground/60 text-xs">{t("invite.policyPlaceholder")}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
